import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { gate } from 'hub-http/gates';
import styled from 'styled-components';
import { TABLE_HEADER_HEIGHT } from 'foundations-components/transitional/constants';
import Header from './Header';
import { mapNamesToWidths } from '../utils/mapNamesToWidths';
import TableBody from './TableBody';
import { getTableMinWidth, makeGetColumnWidth, makeGetFrozenOffsets, setAllWidths } from '../../resizing/redux/widthsSlice';
import { ProvideObserver } from '../../virtualization/context/observerContext';
import { useWidthsDispatch, useWidthsSelector, useSelectionSelector, useSelectionDispatch } from '../../redux/reduxHooks';
import { setRowRegistry } from '../redux/rowRegistrySlice';
import SelectAllCheckboxHeader from '../../selection/components/SelectAllCheckboxHeader';
import TableTopBar from './TableTopBar';
import { CUSTOM_TABLE_HEADER_HEIGHT, SELECTION_COLUMN_WIDTH, TABLE_PAGINATOR_HEIGHT, TABLE_TOP_BAR_HEIGHT } from '../constants/sizes';
import { Box } from 'foundations-components/transitional/components/Box';
import Paginator from '../../pagination/components/Paginator';
import { LoadingOverlay } from 'foundations-components/transitional/components/LoadingOverlay';
import { TableScrollContainer, TableBox, NoFirstDivChildPadding } from './StyledTableComponents';
import { getColumnKey } from '../utils/getColumnKey';
import { useWatchTableContainer } from '../hooks/useWatchTableContainer';
import { getShouldShowBulkActionsContainer } from '../../selection/redux/selectionSlice';
import SelectQueryRow from '../../selection/components/SelectQueryRow';
import { Flex } from 'foundations-components/components/Flex';
import { useShowSelectQuery } from '../hooks/useShowSelectQuery';
import { useFrozenColumnWidthAlert } from '../hooks/useFrozenColumnWidthAlert';
import { useAuth } from '../../auth/context/AuthContext';
import TableFooter from './TableFooter';
import { infiniteScrollDisabled, isInfiniteScrollEnabled } from '../../infiniteScroll/config';

// Extra specificity is necessary to override the default height
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledTR = styled.tr.withConfig({
  displayName: "Table__StyledTR"
})(["&& th{height:", ";}"], ({
  $hasNewTableHeader
}) => $hasNewTableHeader ? CUSTOM_TABLE_HEADER_HEIGHT : TABLE_HEADER_HEIGHT);
const Table = ({
  alternateHeaderLeftSlot,
  BulkActionsComponent,
  canSelectAllRecords,
  columns,
  condensed,
  dataDensity,
  CustomSelectAllComponent,
  disableScrollReset,
  getRowId,
  headerLeftSlot,
  headerRightSlot,
  infiniteScrollConfig = infiniteScrollDisabled,
  isBulkActionsRedesignEnabled,
  isIndexRedesignEnabled,
  loading,
  noRowsSlot,
  numberOfFrozenColumns,
  onColumnOrderChange,
  onColumnWidthChange,
  onPageIndexChange,
  onPageSizeChange,
  onSortsChange,
  pageable: showPagination,
  pageIndex,
  pageSize,
  pageSizes,
  paginatorAlignment,
  RowComponent,
  rows,
  scrollContainerRef,
  selectable,
  sorts,
  TableComponent,
  totalRowCount,
  zebraStripe,
  enableFullVirtualization,
  onFrozenColumnWidthAlert,
  hasTopBarBorderRadius
}) => {
  const rowIds = useMemo(() => rows.map(getRowId), [getRowId, rows]);
  const {
    hasGate
  } = useAuth();
  const hasNewTableHeader = hasGate(gate('CRM:Grid:TableHeader'));
  const hasInfiniteScrollGate = hasGate(gate('CRM:Grid:InfiniteScroll'));
  const {
    observer,
    handleTableContainerRef
  } = useWatchTableContainer({
    rowIds,
    disableScrollReset,
    scrollContainerRef
  });
  const scrollContainerElementRef = useRef(null);
  const handleScrollContainerRef = useCallback(element => {
    scrollContainerElementRef.current = element;
    handleTableContainerRef(element);
  }, [handleTableContainerRef]);
  const initializedWidths = useRef(false);
  const widthsDispatch = useWidthsDispatch();
  const selectionDispatch = useSelectionDispatch();
  useLayoutEffect(() => {
    // We only need to set columns widths if the columns changed
    if (!initializedWidths.current) {
      initializedWidths.current = true;
      return;
    }
    widthsDispatch(setAllWidths(mapNamesToWidths(columns)));
  }, [columns, widthsDispatch]);
  useLayoutEffect(() => {
    // Update the row registry when rowIds change
    selectionDispatch(setRowRegistry(rowIds));
  }, [selectionDispatch, rowIds]);
  const sortMap = useMemo(() => sorts.reduce((acc, {
    columnNamespace,
    columnName,
    direction
  }) => {
    acc[getColumnKey(columnNamespace, columnName)] = direction;
    return acc;
  }, {}), [sorts]);
  const tableMinWidth = useWidthsSelector(getTableMinWidth) + (selectable ? SELECTION_COLUMN_WIDTH : 0);

  // TODO: Add multisort when the index page can handle it
  const handleSortChange = useCallback(({
    columnNamespace: columnNamespace_0,
    columnName: columnName_0,
    direction: direction_0
  }) => {
    onSortsChange({
      nextSorts: [{
        columnNamespace: columnNamespace_0,
        columnName: columnName_0,
        direction: direction_0
      }]
    });
  }, [onSortsChange]);
  const {
    showSelectQueryInBulkActionsContainer,
    showSelectQueryRow
  } = useShowSelectQuery(rows, getRowId, canSelectAllRecords, isBulkActionsRedesignEnabled, totalRowCount);
  const shouldShowBulkActionsInTopBar = useSelectionSelector(getShouldShowBulkActionsContainer) && (!isBulkActionsRedesignEnabled || !isIndexRedesignEnabled);
  const hasHeaderSlots = Boolean(headerLeftSlot || headerRightSlot);
  const showTopBar = shouldShowBulkActionsInTopBar || hasHeaderSlots;
  const tableBoxHasTopBorderRadius = !showTopBar;
  const infiniteScrollEnabled = isInfiniteScrollEnabled(infiniteScrollConfig);
  const hasFooter = hasInfiniteScrollGate && infiniteScrollEnabled;
  const handleInfiniteScroll = () => {
    if (infiniteScrollEnabled && !infiniteScrollConfig.isLoadingNextCursor) {
      infiniteScrollConfig.onNextCursor(infiniteScrollConfig.nextCursor);
    }
  };

  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- The linter flags this because it can't prove the compiler will preserve the useMemo semantics for the selector factory returned by createSelector (which has its own internal memoization cache). It's actually OK because even if the compiler re-creates the selector, useWidthsSelector will still return the correct value — only the selector's internal cache is lost, which is a perf-only concern, not a correctness one.
  const getLastColumnWidth = useMemo(() => {
    // HACK: Some consumers will pass an empty columns array. In this case we'll
    // just return 0 since without columns the table doesn't do anything interesting anyway.
    const lastColumn = columns[columns.length - 1];
    if (!lastColumn) {
      return () => 0;
    }
    return makeGetColumnWidth(getColumnKey(lastColumn.columnNamespace, lastColumn.columnName));
  }, [columns]);
  const lastColumnWidth = useWidthsSelector(getLastColumnWidth);
  const requiredTableProps = useMemo(() => ({
    // HACK: This is required for compat with onboarding tours. Once
    // we have renamed the contact "name" column, we can PR their library
    // to use the actual external id selector and remove this one.
    'data-onboarding': 'grid-table',
    'data-table-external-id': 'framework-data-table',
    'data-test-id': 'framework-data-table',
    'data-test-loading': loading,
    lastColumnWidth,
    minWidth: tableMinWidth
  }), [loading, tableMinWidth, lastColumnWidth]);

  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- Same rationale as getLastColumnWidth above.
  const getFrozenOffsets = useMemo(() => {
    if (numberOfFrozenColumns === 0) {
      return () => [];
    }
    const columnKeys = columns.map(column => {
      return getColumnKey(column.columnNamespace, column.columnName);
    }).slice(0, numberOfFrozenColumns - 1);
    return makeGetFrozenOffsets(columnKeys, selectable);
  }, [columns, numberOfFrozenColumns, selectable]);
  const frozenOffsets = useWidthsSelector(getFrozenOffsets);
  useFrozenColumnWidthAlert(columns, frozenOffsets, scrollContainerRef, onFrozenColumnWidthAlert);
  const thElements = useMemo(() => {
    return columns.map((column_0, index) => {
      const key = getColumnKey(column_0.columnNamespace, column_0.columnName);
      return /*#__PURE__*/_jsx(Header, {
        column: column_0,
        columnIndex: index,
        columnPosition: frozenOffsets[index],
        isLastColumn: index === columns.length - 1,
        sortDirection: sortMap[key],
        onSortChange: handleSortChange,
        onColumnWidthChange: onColumnWidthChange,
        onColumnOrderChange: onColumnOrderChange,
        numberOfFrozenColumns: numberOfFrozenColumns
      }, key);
    });
  }, [columns, frozenOffsets, handleSortChange, numberOfFrozenColumns, onColumnOrderChange, onColumnWidthChange, sortMap]);
  return /*#__PURE__*/_jsxs(Flex, {
    blockSize: "100%",
    "data-test-id": "framework-data-table-container",
    flexDirection: "column",
    inlineSize: "100%",
    overflow: "hidden",
    children: [showTopBar && /*#__PURE__*/_jsx(Box, {
      shrink: 0,
      basis: TABLE_TOP_BAR_HEIGHT,
      inlineSize: "100%",
      children: /*#__PURE__*/_jsx(TableTopBar, {
        alternateHeaderLeftSlot: alternateHeaderLeftSlot,
        BulkActionsComponent: BulkActionsComponent,
        headerLeftSlot: headerLeftSlot,
        headerRightSlot: headerRightSlot,
        isBulkActionsRedesignEnabled: isBulkActionsRedesignEnabled,
        isIndexRedesignEnabled: isIndexRedesignEnabled,
        showSelectQuery: showSelectQueryInBulkActionsContainer,
        totalRowCount: totalRowCount,
        hasTopBarBorderRadius: hasTopBarBorderRadius
      })
    }), /*#__PURE__*/_jsxs(TableBox, {
      showPagination: showPagination,
      showTopBar: showTopBar,
      loading: loading,
      hasTopBorderRadius: tableBoxHasTopBorderRadius,
      hasNewTableHeader: hasNewTableHeader,
      children: [loading && /*#__PURE__*/_jsx(NoFirstDivChildPadding, {
        children: /*#__PURE__*/_jsx(LoadingOverlay, {
          "data-test-id": "table-loading",
          contextual: true
        })
      }), rows.length > 0 ? /*#__PURE__*/_jsx(TableScrollContainer, {
        ref: handleScrollContainerRef,
        children: /*#__PURE__*/_jsx(ProvideObserver, {
          value: observer,
          children: /*#__PURE__*/_jsxs(TableComponent, {
            numberOfFrozenColumns: numberOfFrozenColumns,
            frozenOffsets: frozenOffsets,
            requiredTableProps: requiredTableProps,
            zebraStripe: zebraStripe,
            condensed: condensed,
            dataDensity: dataDensity,
            children: [/*#__PURE__*/_jsxs("thead", {
              children: [/*#__PURE__*/_jsxs(StyledTR, {
                $hasNewTableHeader: hasNewTableHeader,
                children: [selectable && /*#__PURE__*/_jsx(SelectAllCheckboxHeader, {
                  rowIds: rowIds
                }), thElements]
              }), showSelectQueryRow && /*#__PURE__*/_jsx(SelectQueryRow, {
                rowIds: rowIds,
                totalRowCount: totalRowCount,
                columnCount: columns.length + (selectable ? 1 : 0),
                CustomSelectAllComponent: CustomSelectAllComponent
              })]
            }), /*#__PURE__*/_jsx(TableBody, {
              columns: columns,
              dataDensity: dataDensity,
              getRowId: getRowId,
              numberOfFrozenColumns: numberOfFrozenColumns,
              pageable: showPagination,
              pageSize: pageSize,
              RowComponent: RowComponent,
              rows: rows,
              selectable: selectable,
              enableFullVirtualization: enableFullVirtualization
            }), hasFooter && /*#__PURE__*/_jsx(TableFooter, {
              infiniteScrollConfig: infiniteScrollConfig,
              handleIntersectionChange: handleInfiniteScroll,
              scrollContainerRef: scrollContainerElementRef
            })]
          })
        })
      }) : noRowsSlot]
    }), showPagination && /*#__PURE__*/_jsx(Box, {
      shrink: 0,
      basis: TABLE_PAGINATOR_HEIGHT,
      inlineSize: "100%",
      children: /*#__PURE__*/_jsx(Paginator, {
        pageIndex: pageIndex,
        pageSize: pageSize,
        pageSizes: pageSizes,
        paginatorAlignment: paginatorAlignment,
        totalRowCount: totalRowCount,
        onPageIndexChange: onPageIndexChange,
        onPageSizeChange: onPageSizeChange
      })
    })]
  });
};
export default Table;